import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMercenary } from '../../contexts/MercenaryContext';
import { parseCombatTag, isRoundHeader, isRewardLine } from '../../utils/combatLogUtils';
import { formatDateTimeUTC } from '../../utils/dateFormat';
import BattleViewer2D, { BattleData } from './Admin/BattleViewer2D';
import styles from './BattleResult.module.css';

type BattleLogMode = 'new2d' | 'scrolling' | 'instant';

interface ParticipantSummary {
  combatantId: number;
  name?: string;
  team: string;
  type: string;
  level?: number;
  isBot?: boolean;
  damage: number;
  highestDamageDealt: number;
  damageTaken: number;
  attacksMade: number;
  dodgedAttacks: number;
  parries: number;
  blockedAttacks: number;
  missedAttacks: number;
  criticalHits: number;
  taunts?: number;
}

interface BattleResultData {
  reportData: {
    participantSummaries: { [key: string]: ParticipantSummary };
    teamSummaries: {
      teamOne: any;
      teamTwo: any;
    };
    combatLog: string[];
    seasonName: string;
    winner: string;
  };
  title: string;
  createdAt: string;
  guildInfo: { [mercenaryId: string]: { guildId: number | null; guildName: string | null; guildTag: string | null } };
}

const BattleResult: React.FC = () => {
  const { battleId } = useParams<Record<string, string>>();
  const navigate = useNavigate();
  const { setMercenary, mercenaryId: contextMercenaryId } = useMercenary();
  const playerMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0');
  const [battleResult, setBattleResult] = useState<BattleResultData | null>(null);
  const [playerTeam, setPlayerTeam] = useState<string>('');
  const [playerGuildId, setPlayerGuildId] = useState<number | null>(null);
  const [battleLogMode, setBattleLogMode] = useState<BattleLogMode>('new2d');
  const [battleViewSpeed, setBattleViewSpeed] = useState(1);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [viewingComplete, setViewingComplete] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshMercenary = useCallback(async () => {
    const mercId = contextMercenaryId || playerMercenaryId;
    if (!mercId) return;
    try {
      const response = await axios.get(`/api/mercenary/${mercId}`, { withCredentials: true });
      if (response.data) {
        setMercenary(response.data);
      }
    } catch (err) {
      // Non-critical, don't block battle result display
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleId]);

  const handleViewingComplete = useCallback(() => {
    setViewingComplete(true);
    refreshMercenary();
  }, [refreshMercenary]);

  // Fetch user settings to determine battle log mode
  useEffect(() => {
    axios.get('/api/user/settings', { withCredentials: true })
      .then(res => {
        if (res.data.battleLogMode) {
          setBattleLogMode(res.data.battleLogMode as BattleLogMode);
        }
        if (res.data.battleViewSpeed) {
          setBattleViewSpeed(Number(res.data.battleViewSpeed));
        }
      })
      .catch(() => {/* use default 'new2d' */})
      .finally(() => setSettingsLoaded(true));
  }, []);

  // Refresh mercenary stats, only for instant mode, only after settings are confirmed loaded
  // For scrolling / new2d, refreshMercenary is called on handleViewingComplete
  useEffect(() => {
    if (!settingsLoaded) return;
    if (battleLogMode === 'instant') {
      refreshMercenary();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded]);

  const fetchBattleResult = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || ''}/api/battle/result/${battleId}`, { withCredentials: true });
      const resultData = response.data;
      setBattleResult(resultData);

      const playerSummary = (Object.values(resultData.reportData.participantSummaries) as ParticipantSummary[]).find(
        (participant) => participant.combatantId === playerMercenaryId
      );
      if (playerSummary) {
        setPlayerTeam((playerSummary as ParticipantSummary).team);
      }
      
      // Get player's guild ID
      if (resultData.guildInfo && resultData.guildInfo[playerMercenaryId]) {
        setPlayerGuildId(resultData.guildInfo[playerMercenaryId].guildId);
      }
    } catch (error) {
      console.error('Error fetching battle result:', error);
    }
  };

  useEffect(() => {
    if (!battleResult) {
      fetchBattleResult();
    }
  }, [battleId, battleResult]);

  // Scrolling mode: advance one log entry every ~80 ms
  useEffect(() => {
    if (battleLogMode !== 'scrolling' || !battleResult || viewingComplete) return;

    const combatData = battleResult.reportData.combatLog;
    if (!combatData || combatData.length === 0) {
      handleViewingComplete();
      return;
    }

    if (revealedCount >= combatData.length) {
      handleViewingComplete();
      return;
    }

    scrollIntervalRef.current = setInterval(() => {
      setRevealedCount(prev => {
        const next = prev + 1;
        if (next >= combatData.length) {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
          handleViewingComplete();
        }
        return next;
      });
    }, Math.max(50, Math.round(550 / battleViewSpeed)));

    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleLogMode, battleResult, battleViewSpeed]);

  const skipScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    if (battleResult) {
      setRevealedCount(battleResult.reportData.combatLog.length);
    }
    handleViewingComplete();
  }, [battleResult, handleViewingComplete]);

  const getParticipantClass = (id: number) => {
    if (!battleResult) return 'enemyTeam1';
  
    const participant = Object.values(battleResult.reportData.participantSummaries).find(
      (p: any) => p.combatantId === id
    );

    if (!participant) return 'enemyTeam1';
    
    // Player's mercenary is always green
    if (id === playerMercenaryId && participant.type === 'mercenary') return 'playerMerc';
    
    // Player's team is always blue
    if (participant.team === playerTeam) return 'playerTeam';
    
    // Enemy teams get different colors (red, yellow, orange based on team)
    // Get unique enemy teams and assign colors
    const allParticipants = Object.values(battleResult.reportData.participantSummaries) as ParticipantSummary[];
    const enemyTeams = [...new Set(allParticipants
      .filter((p: any) => p.team !== playerTeam)
      .map((p: any) => p.team))];
    
    const teamIndex = enemyTeams.indexOf(participant.team);
    if (teamIndex === 0) return 'enemyTeam1';
    if (teamIndex === 1) return 'enemyTeam2';
    if (teamIndex === 2) return 'enemyTeam3';
    
    return 'enemyTeam1';
  };
  

  const renderTeamPreview = (teamType: string, participantSummaries: any) => {
    if (!participantSummaries) return null;

    const teamMembers = Object.values(participantSummaries).filter(
      (participant: any) => participant.team === teamType
    );

    return (
      <div className={styles.teamPreview}>
        <h4>{teamType === 'teamOne' ? 'Team One' : 'Team Two'}</h4>
        <ul>
          {teamMembers.map((participant: any, index: number) => {
            const isGuildMate = battleResult?.guildInfo && 
              playerGuildId && 
              battleResult.guildInfo[participant.combatantId]?.guildId === playerGuildId &&
              participant.combatantId !== playerMercenaryId;
            
            const colorClass = getParticipantClass(participant.combatantId);
            const classToApply = (styles as any)[colorClass] || styles.participant;
            return (
              <li
                key={index}
                className={classToApply}
              >
                {participant.isBot && <span className={styles.botBadge}>B</span>}
                {isGuildMate && <span className={styles.guildCrown}>♔</span>}
                {participant.name} (Level {participant.level || 'N/A'})
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const parseLogWithColors = (log: string) => {
    // Strip @@tag@@ prefix before parsing color tags
    const { text } = parseCombatTag(log);
    return text.split(/(\{\{.*?\}\})/).map((segment, i) => {
      const match = segment.match(/\{\{(.*?)\}\}/);
      if (match) {
        const [, id, name] = match[1].split(':');
        const combatantId = parseInt(id);
        const colorClass = getParticipantClass(combatantId);
        
        // Check if this mercenary is a guild mate
        const isGuildMate = battleResult?.guildInfo && 
          playerGuildId && 
          battleResult.guildInfo[combatantId]?.guildId === playerGuildId &&
          combatantId !== playerMercenaryId;

        // Check if this combatant is a bot
        const botSummary = battleResult?.reportData?.participantSummaries &&
          (Object.values(battleResult.reportData.participantSummaries) as ParticipantSummary[]).find(
            (p) => p.combatantId === combatantId
          );
        const isBot = botSummary?.isBot;
        
        // Apply the color class directly - use styles object or fallback
        const classToApply = (styles as any)[colorClass] || styles.participant;
        
        return (
          <span key={i} className={classToApply}>
            {isBot && <span className={styles.botBadge}>B</span>}
            {isGuildMate && <span className={styles.guildCrown}>♔</span>}
            {name}
          </span>
        );
      }
      return <span key={i}>{segment}</span>;
    });
  };

  const parseRewardLine = (line: string): React.ReactNode => {
    // Strip @@tag@@ prefix before parsing reward content
    const { text: cleanLine } = parseCombatTag(line);
    const receivesIdx = cleanLine.indexOf(' receives ');
    if (receivesIdx === -1) return <>{cleanLine}</>;

    const name = cleanLine.substring(0, receivesIdx);
    const rest = cleanLine.substring(receivesIdx + ' receives '.length);

    const byDefeatingIdx = rest.indexOf(' by defeating ');
    const mainPart = byDefeatingIdx !== -1
      ? rest.substring(0, byDefeatingIdx)
      : rest.replace(/\.$/, '');
    const beastsPart = byDefeatingIdx !== -1
      ? rest.substring(byDefeatingIdx + ' by defeating '.length).replace(/\.$/, '')
      : null;

    // For PvE lines the '&' separates XP/silver from items; for PvP it's part of the stats string
    const ampIdx = beastsPart !== null ? mainPart.indexOf(' & ') : -1;
    const xpSilverPart = ampIdx !== -1 ? mainPart.substring(0, ampIdx) : mainPart;
    const itemsPart = ampIdx !== -1 ? mainPart.substring(ampIdx + 3) : null;

    // Detect and extract (pooled) and (N rested) tags from the stats string
    const pooledMatch = xpSilverPart.match(/ \(pooled\)/);
    const restedMatch = xpSilverPart.match(/ \((\d+) rested\)/);
    const cleanStats = xpSilverPart
      .replace(/ \(pooled\)/, '')
      .replace(/ \(\d+ rested\)/, '');

    return (
      <>
        <span className={styles.rewardName}>{name}</span>
        {' receives '}
        <span className={styles.rewardStats}>
          {cleanStats}
          {restedMatch && (
            <span
              className={styles.restedTag}
              data-tooltip={`+${restedMatch[1]} bonus XP from rested experience`}
              tabIndex={0}
            >{restedMatch[1]} rested</span>
          )}
          {pooledMatch && (
            <span
              className={styles.pooledTag}
              data-tooltip="XP stored in pool, paid out after the tournament ends"
              tabIndex={0}
            >pooled</span>
          )}
        </span>
        {itemsPart && (
          <>
            {' & '}
            {itemsPart.split(', ').map((item, i, arr) => (
              <React.Fragment key={i}>
                <span className={styles.rewardItem}>{item}</span>
                {i < arr.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </>
        )}
        {beastsPart ? (
          <>
            {' by defeating '}
            <span className={styles.rewardBeasts}>{beastsPart}</span>
            {'.'}
          </>
        ) : '.'}
      </>
    );
  };

  const renderRewards = () => {
    const rewardsLog = battleResult?.reportData?.combatLog?.filter((log: string) => {
      const { eventType, text } = parseCombatTag(log);
      return isRewardLine(eventType, text);
    });
    return (
      <div className={styles.rewardsSection}>
        <h4>Battle Rewards</h4>
        {rewardsLog && rewardsLog.map((reward: string, index: number) => (
          <p key={index}>{parseRewardLine(reward)}</p>
        ))}
      </div>
    );
  };

  const renderSummaryTable = (teamType: string, teamSummary: any, participantSummaries: any) => {
    if (!participantSummaries || !teamSummary) {
      return null;
    }

    // Show taunts column only if any participant landed a taunt
    const anyTaunts = Object.values(participantSummaries).some((p: any) => (p.taunts ?? 0) > 0);

    return (
      <div className={styles.tableContainer}>
        <table className={styles.summaryTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Total Dmg</th>
              <th>Largest Hit</th>
              <th>Dmg Taken</th>
              <th>Attacks</th>
              <th>Dodges</th>
              <th>Parries</th>
              <th>Blocks</th>
              <th>Missed</th>
              <th>Critical</th>
              {anyTaunts && <th>Taunts</th>}
            </tr>
          </thead>
        <tbody>
          {Object.keys(participantSummaries).map((key) => {
            const participant = participantSummaries[key];
            if (participant.team === teamType) {
              return (
                <tr key={key}>
                  <td>{participant.name}</td>
                  <td>{participant.damage}</td>
                  <td>{participant.highestDamageDealt}</td>
                  <td>{participant.damageTaken}</td>
                  <td>{participant.attacksMade}</td>
                  <td>{participant.dodgedAttacks}</td>
                  <td>{participant.parries}</td>
                  <td>{participant.blockedAttacks}</td>
                  <td>{participant.missedAttacks}</td>
                  <td>{participant.criticalHits}</td>
                  {anyTaunts && <td>{participant.taunts ?? 0}</td>}
                </tr>
              );
            }
            return null;
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Team Total</td>
            <td>{teamSummary.totalDamage}</td>
            <td>{teamSummary.highestDamageDealt}</td>
            <td>{teamSummary.damageTaken}</td>
            <td>{teamSummary.attacksMade}</td>
            <td>{teamSummary.dodgedAttacks}</td>
            <td>{teamSummary.parries}</td>
            <td>{teamSummary.blockedAttacks}</td>
            <td>{teamSummary.missedAttacks}</td>
            <td>{teamSummary.criticalHits}</td>
            {anyTaunts && <td>{teamSummary.taunts ?? 0}</td>}
          </tr>
        </tfoot>
        </table>
      </div>
    );
  };

  const showSummary = settingsLoaded && battleLogMode !== 'new2d' && (battleLogMode === 'instant' || viewingComplete);

  return (
    <div className={styles.battleContainer}>
      {battleResult && battleResult.reportData ? (
        <>
          {/* Info Panel */}
          <div className={styles.infoPanel}>
            <h2 className={styles.battleTitle}>
              <span className={styles.battleTitleLine} />
              <span className={styles.battleTitleText}>{battleResult.title}</span>
              <span className={styles.battleTitleLine} />
            </h2>
            <p className={styles.battleMeta}>
              <span className={styles.battleMetaSeason}>{battleResult.reportData.seasonName}</span>
              <span className={styles.battleMetaSep}>—</span>
              <span>{formatDateTimeUTC(battleResult.createdAt)} UTC</span>
            </p>
            {battleLogMode !== 'new2d' && (
              <div className={styles.teamPreviews}>
                {renderTeamPreview('teamOne', battleResult.reportData.participantSummaries)}
                {renderTeamPreview('teamTwo', battleResult.reportData.participantSummaries)}
              </div>
            )}
          </div>

          {/* 2D Viewer mode, stays mounted after battle ends so summary appears below */}
          {battleLogMode === 'new2d' && settingsLoaded && (
            <BattleViewer2D
              initialBattleData={battleResult as unknown as BattleData}
              onComplete={handleViewingComplete}
              myMercenaryId={playerMercenaryId || undefined}
            />
          )}

          {/* Scrolling mode */}
          {battleLogMode === 'scrolling' && !viewingComplete && (
            <>
              <div className={styles.combatLog}>
                {battleResult.reportData.combatLog
                  .slice(0, revealedCount)
                  .filter((log: string) => {
                    const { eventType, text } = parseCombatTag(log);
                    return !isRewardLine(eventType, text);
                  })
                  .map((log: string, index: number) => {
                    const { eventType, text } = parseCombatTag(log);
                    if (isRoundHeader(eventType, text)) {
                      const display = text.replace(/---/g, '').trim();
                      return (
                        <div key={index} className={styles.roundHeader}>
                          {display}
                        </div>
                      );
                    }
                    return <p key={index}>{parseLogWithColors(log)}</p>;
                  })}
              </div>
              <div className={styles.fightAgainRow}>
                <button className={styles.fightAgainBtn} onClick={skipScrolling}>
                  Skip to Results
                </button>
              </div>
            </>
          )}

          {/* Instant mode, full log immediately */}
          {battleLogMode === 'instant' && (
            <div className={styles.combatLog}>
              {battleResult.reportData.combatLog
                .filter((log: string) => {
                  const { eventType, text } = parseCombatTag(log);
                  return !isRewardLine(eventType, text);
                })
                .map((log: string, index: number) => {
                  const { eventType, text } = parseCombatTag(log);
                  if (isRoundHeader(eventType, text)) {
                    const display = text.replace(/---/g, '').trim();
                    return (
                      <div key={index} className={styles.roundHeader}>
                        {display}
                      </div>
                    );
                  }
                  return <p key={index}>{parseLogWithColors(log)}</p>;
                })}
            </div>
          )}

          {/* Summary and rewards, shown immediately for instant; after viewing for other modes */}
          {showSummary && (
            <>
              <h3 className={styles.summaryHeading}>
                {battleResult.reportData.winner === 'teamOne' ? 'Team One Wins!' : 'Team Two Wins!'}
              </h3>
              <div className={styles.summaryTables}>
                {renderSummaryTable(
                  'teamOne',
                  battleResult.reportData.teamSummaries.teamOne,
                  battleResult.reportData.participantSummaries
                )}
                {renderSummaryTable(
                  'teamTwo',
                  battleResult.reportData.teamSummaries.teamTwo,
                  battleResult.reportData.participantSummaries
                )}
              </div>

              {/* Rewards Section */}
              {renderRewards()}

              {/* Fight Again – beast battles only */}
              {battleResult.title?.includes('Beast Battle') && (
                <div className={styles.fightAgainRow}>
                  <button
                    className={styles.fightAgainBtn}
                    onClick={() => {
                      const beastName = battleResult.title.replace('Beast Battle - ', '').trim();
                      navigate('/beasts', { state: { autoFight: beastName } });
                    }}
                  >
                    Fight Again
                  </button>
                </div>
              )}

              {/* Create New Ranked Lobby – ranked battles only */}
              {battleResult.title?.includes('Ranked') && (
                <div className={styles.fightAgainRow}>
                  <button
                    className={styles.fightAgainBtn}
                    onClick={() => navigate('/ranked-battle/create')}
                  >
                    Create New Ranked Lobby
                  </button>
                </div>
              )}

              {/* Create New Lobby – team battles only (not ranked) */}
              {battleResult.title?.includes('Team Battle') && !battleResult.title?.includes('Ranked') && (
                <div className={styles.fightAgainRow}>
                  <button
                    className={styles.fightAgainBtn}
                    onClick={() => navigate('/team-battle/create')}
                  >
                    Create New Lobby
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>Loading...</p>
      )}
    </div>
  );
};

export default BattleResult;